/* "Save as template" from the report workspace: name, description, category
 * and whether the template keeps this report's data connections. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, LayoutTemplate } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { summarizeDefinition, templatesApi } from "../../api/v21";
import { LayoutThumb, describeVisuals } from "../../components/LayoutThumb";
import { Banner, Button, Field, Input, Modal, Textarea, useToast } from "../../ds";

export function SaveAsTemplateDialog({ open, onClose, reportId, reportName, definition }: { open: boolean; onClose: () => void; reportId: string; reportName: string; definition: Record<string, unknown> | null | undefined }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [keep, setKeep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setName(`${reportName} layout`);
      setDescription("");
      setCategory("");
      setKeep(false);
      setError(null);
    }
  }, [open, reportName]);
  const summary = useMemo(() => summarizeDefinition(definition), [definition]);
  const save = useMutation({
    mutationFn: () => templatesApi.create({ name: name.trim(), description: description.trim() || undefined, category: category.trim() || undefined, definition: definition as Record<string, unknown>, keepBindings: keep, sourceReportId: reportId }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Saved as a template", `“${t.name}” is ready in Templates and in New report.`);
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Couldn't save the template."),
  });
  const option = (value: boolean, Icon: typeof Database, title: string, text: string) => (
    <label className="ds-card" style={{ display: "flex", gap: 12, padding: "12px 14px", cursor: "pointer", boxShadow: keep === value ? "var(--focus-ring)" : "none", borderColor: keep === value ? "var(--accent)" : undefined }}>
      <input type="radio" name="keep-bindings" checked={keep === value} onChange={() => setKeep(value)} style={{ marginTop: 3 }} />
      <Icon size={18} className="ds-faint" style={{ marginTop: 1, flex: "none" }} />
      <div>
        <div style={{ fontWeight: 650 }}>{title}</div>
        <div className="ds-small ds-muted">{text}</div>
      </div>
    </label>
  );
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Save as template"
      description="Reuse this report's layout for new reports. The report itself doesn't change."
      footer={
        <>
          <Button variant="ghost" onClick={() => { onClose(); navigate("/templates"); }} style={{ marginRight: "auto" }}>Manage templates</Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={LayoutTemplate} loading={save.isPending} disabled={!name.trim() || !definition} onClick={() => save.mutate()}>Save template</Button>
        </>
      }
    >
      <div className="ds-grid" style={{ gridTemplateColumns: "220px minmax(0, 1fr)", alignItems: "start", gap: 20 }}>
        <div>
          <LayoutThumb summary={summary} height={124} title="This report's first page" />
          <div className="ds-small ds-faint" style={{ marginTop: 8 }}>{describeVisuals(summary.visualTypes)}{summary.pageCount > 1 ? ` · ${summary.pageCount} pages` : ""}</div>
        </div>
        <div className="ds-stack">
          <Field label="Template name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus /></Field>
          <div className="ds-grid ds-grid--2">
            <Field label="Category" hint="e.g. Finance, HR, Operations"><Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={64} /></Field>
            <Field label="Description" hint="What is this layout good for?"><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ minHeight: 36 }} maxLength={1024} /></Field>
          </div>
          <div className="ds-stack" style={{ gap: 8 }}>
            {option(false, LayoutTemplate, "Layout only (remove data)", "Keeps the pages, visual types, sizes, titles and look. Every visual starts empty so it can be connected to any data. Shared with everyone who builds reports.")}
            {option(true, Database, "Keep data connections", `Visuals stay connected to the same datasets${summary.datasetCount ? ` (${summary.datasetCount})` : ""} — a quick copy for a similar report. Only you and admins will see it.`)}
          </div>
          {error ? <Banner tone="danger" title="Couldn't save the template">{error}</Banner> : null}
        </div>
      </div>
    </Modal>
  );
}
